import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import CreateNewArtlist from '@/shared-components/forms/components/CreateNewArtlist';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import Text from '@/shared-components/text/components/Text';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import MetaDescripProfilePages from '../MetaDescripProfilePages';

import ArtlistsVirtualList from './components/ArtlistsVirtualList';

import './index.css';

export default function ProfileArtlists() {
  const { profileData, internal } = useContext(ProfileDataContext);
  const t = useTranslations('common.profile.pages.artlists');
  const tProfileAddButton = useTranslations('common.shared.profile_add_button');
  const { state } = useStateHandler();
  const { loading, fetchData, loadMoreData } = useCache(
    `${profileData._id}_artlists`,
    `${config.apis.api.url}/artlist/user/${profileData._id}?view=profile_list`,
    {
      injectToken: true,
      invalidateWhen: internal
        ? [
          "NEW_ARTLIST",
          "EDIT_ARTLIST",
          "DELETE_ARTLIST",
          "ADD_TO_ARTLIST",
          "ADD_TO_ARTLIST_FROM_ARTWORK",
          "REMOVE_FROM_ARTLIST",
          `${profileData._id}_REFRESH`,
        ]
        : [`${profileData._id}_REFRESH`],
    }
  );
  const [openCreateArtlist, setOpenCreateArtlist] = useState(false);
  const [showSuartePlusPopup, setShowSuartePlusPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const navigate = useNavigate();

  const isGallery = profileData?.user_type === "gallery";
  const session = state.user_session;
  const canCreateArtlist = internal && (!isGallery
    || canAccessGalleryPermission(session, "artlist.create", profileData?._id));
  const hasSuartePlus = Boolean(profileData?.suartePlus?.isActive);
  const artlistsCount = fetchData?.totalDocs ?? fetchData?.data?.length ?? 0;
  const reachedArtlistLimit = !isGallery && !hasSuartePlus && artlistsCount >= 3;

  function onCreateNewArtlist(newArtlist) {
    navigate(`/artlist/${newArtlist._id}`, {
      state: { from: true },
    });
  }

  function handleAddArtlistClick() {
    if (reachedArtlistLimit) {
      setShowSuartePlusPopup(true);
      return;
    }
    if (!canCreateArtlist) {
      setShowActionRejectedPopup(true);
      return;
    }
    setOpenCreateArtlist(true);
  }

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="artlists"
        internal={internal}
        itemCount={artlistsCount}
      />
      {internal && (
        <>
          <ProfileAddButton
            onClick={handleAddArtlistClick}
            label={tProfileAddButton('create_artlist')}
          />
          <CreateNewArtlist
            open={openCreateArtlist}
            close={() => setOpenCreateArtlist(false)}
            onCreateNewArtlist={onCreateNewArtlist}
          />
          <SuartePlusPopup
            open={showSuartePlusPopup}
            onClose={() => setShowSuartePlusPopup(false)}
            highlightedBenefit="artlists"
            actionTitle={t('suarte_plus.action_title')}
          />
          <ActionRejectedPopup
            open={showActionRejectedPopup}
            close={() => setShowActionRejectedPopup(false)}
            description={t('messages.permission_denied')}
          />
        </>
      )}
      {loading ? (
        <ProfileContentLoader className="profile-artlists__artlists-spinner" />
      ) : fetchData.data.length === 0 ? (
        <Text className="user-profile__artlists-empty-view font-size-medium" paragraph>
          {internal ? (
            profileData.user_type === "gallery" ? (
              t('empty.internal_gallery')
            ) : (
              t('empty.internal_user')
            )
          ) : (
            t('empty.external')
          )}
        </Text>
      ) : (
        <ArtlistsVirtualList items={fetchData} onLoadMore={loadMoreData} />
      )}
    </>
  );
}
